import { ReactElement } from 'react';
import { IconsMap } from '@nx-frontend/shared/ui/icon';
import { AtivityItemMode, NotificationActivityEnrichedRecord, TranslatesFnType } from '@nx-frontend/shared/types';
import { PortalPageBreadcrumb } from '@nx-frontend/shared/types';
import { replaceNimbusDomains } from '@nx-frontend/shared/utils';

/**
 * Get breadcrumbs page
 * @param item
 * @returns
 */
function getBreadcrumbsPart(
  item: NotificationActivityEnrichedRecord,
  type: 'page' | 'domain'
): PortalPageBreadcrumb | undefined {
  return item.extra.breadcrumbs?.find((b) => b.type === type);
}

/**
 * Adds comment routing info to the url hash
 * @param item
 * @param url
 * @returns
 */
function addCommentRouteToUrl(item: NotificationActivityEnrichedRecord, url: string): string {
  if (item.comment) {
    url += `#comment:${item.comment.threadId}_${item.comment.globalId}`;
  }
  return url;
}

type ChatMessageOp = { insert: string | { [key: string]: { [key: string]: string } } };

interface ActivityChatExtra {
  channel: { id: string; name?: string };
  isDM: boolean;
  message?: {
    timestamp: number;
    ops: ChatMessageOp[];
  };
  messages?: {
    timestamp: number;
    ops: ChatMessageOp[];
  }[];
  notificationId: string;
  sender: { id: number };
}

/**
 * Get text from delta
 * @param delta
 * @returns
 */
function deltaToText(delta: ChatMessageOp[]): string {
  let text = '';
  delta.forEach((op) => {
    if (typeof op.insert === 'string') {
      text += op.insert;
    }
    if (typeof op.insert === 'object') {
      if ('mention' in op.insert) {
        text += `@${op.insert.mention.name}`;
      }
    }
  });
  return text;
}

/**
 * Get chat context from message ops
 * @param extra
 * @returns
 */
function getChatContext(extra: ActivityChatExtra): string | null {
  let text = '';
  let message = extra.message;
  if (extra.messages) {
    message = extra.messages[0];
  }
  if (message) {
    text = deltaToText(message.ops);
  }
  return text.length > 100 ? `${text.slice(0, 100)}...` : text;
}

/**
 * Get is chat item disabled
 * @param item
 * @param options
 * @returns
 */
function getChatItemDisabled(
  _item: NotificationActivityEnrichedRecord,
  _options: GetActivityParamsOptions = {}
): boolean {
  return false;
}

/**
 * Get is chat parent disabled
 * @param item
 * @param options
 * @returns
 */
function getChatParentDisabled(
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {}
): boolean {
  return getChatItemDisabled(item, options);
}

/**
 * Get activity item disabled
 * @param item
 * @returns
 */
function getNoteItemDisabled(
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {}
): boolean {
  const { mode } = options;
  let disabled = false;

  if (mode === 'webnotes') {
    disabled = !(
      !(item.action === 'move' && item.extra.noteParentId === 'trash') &&
      item.note &&
      item.parentTitle &&
      item.note?.parentId !== 'trash' &&
      item.noteParent?.parentId !== 'trash' &&
      item.isParentDeleted !== true
    );

    if (item.note?.type === 'folder' && item.note?.isSharedForPortal) {
      disabled = true;
    }
  }

  if (mode === 'portals') {
    disabled = !item.note?.isSharedForPortal;
  }

  return disabled;
}

/**
 * Get note parent disabled
 * @param item
 * @param options
 * @returns
 */
function getNoteParentDisabled(
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {}
): boolean {
  const { mode } = options;
  if (mode === 'portals') return false;
  return !!(item.parentInTrash || (item.note?.isSharedForPortal && !item.parentUrl) || !!item.isParentDeleted);
}

/**
 * Get page title
 * @param item
 * @returns
 */
function getNotePageTitle(item: NotificationActivityEnrichedRecord): string {
  let title = item.extra.noteTitle ?? '-';
  if (item.note?.isSharedForPortal && item.portalPageName) {
    title = item.portalPageName;
  }
  return title;
}

/**
 * Get url for note page
 * @param item
 * @returns
 */
function getNotePageUrl(item: NotificationActivityEnrichedRecord): string {
  const type = item.note?.type === 'folder' ? 'folder' : 'page';
  let url = `/space/${item.workspaceId}/${type}/${item.nodeId}`;
  if (item.portalPageUrl) {
    url = item.portalPageUrl;
  } else if (item.portalPage?.breadcrumbs) {
    url = item.portalPage.breadcrumbs.find((b) => b.type === 'page')?.url ?? '';
  } else if (item.note?.isSharedForPortal && item.extra.breadcrumbs) {
    url = getBreadcrumbsPart(item, 'page')?.url ?? '';
  }
  return url;
}

/**
 * Get note parent url
 * @param item
 * @returns
 */
function getNoteParentUrl(item: NotificationActivityEnrichedRecord): string {
  let url = `/space/${item.workspaceId}/folder/${item.extra.noteParentId}`;
  if (item.note?.isSharedForPortal && item.parentUrl) {
    url = item.parentUrl;
  }
  return url;
}

/**
 * Get url for page with comment
 * @param item
 * @returns
 */
function getCommentPageUrl(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}): string {
  let url = '';
  if (item.comment) {
    url = addCommentRouteToUrl(item, getNotePageUrl(item));
  }
  if (item.task) {
    url = getTaskPageUrl(item, options);
  }
  return url;
}

/**
 * Get comment title
 * @param item
 * @returns
 */
function getCommentTitle(item: NotificationActivityEnrichedRecord): string {
  let title = item.extra.noteTitle ?? '-';
  if (item.task) {
    title = item.task.title;
  }
  return title;
}

/**
 * Get comment parent title
 * @param item
 * @returns
 */
function getCommentParentTitle(item: NotificationActivityEnrichedRecord): string {
  let title = item.parentTitle === 'noParent' ? '' : item.parentTitle ?? '';
  if (item.task) {
    title = 'Tasks';
  }
  return title;
}

/**
 * Get comment parent icon
 * @param item
 * @returns
 */
function getCommentParentIcon(item: NotificationActivityEnrichedRecord): ReactElement {
  let icon = IconsMap.FOLDER;
  if (item.task) {
    icon = IconsMap.TASKS;
  }
  return icon;
}

/**
 * Get comment item disabled
 * @param item
 */
function getCommentItemDisabled(item: NotificationActivityEnrichedRecord): boolean {
  let disabled = getNoteItemDisabled(item);
  if (item.task) {
    disabled = getTaskItemDisabled(item);
  }
  return disabled;
}

/**
 * Get comment parent disabled
 * @param item
 */
function getCommentParentDisabled(item: NotificationActivityEnrichedRecord): boolean {
  let disabled = getNoteParentDisabled(item);
  if (item.task) {
    disabled = false;
  }
  return disabled;
}

/**
 * Get comment parent url
 * @param item
 * @param options
 * @returns
 */
function getCommentParentUrl(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}): string {
  let url = getNoteParentUrl(item);
  if (item.task) {
    url = getTaskParentUrl(item, options);
  }
  return url;
}

/**
 * Get comment open portal page in new tab
 * @param item
 * @param options
 * @returns
 */
function getCommentOpenPortalPageInNewTab(
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {}
): boolean {
  let openPortalPageInNewTab = true;
  if (item.task) {
    openPortalPageInNewTab = getTaskOpenPortalPageInNewTab(item, options);
  }
  return openPortalPageInNewTab;
}

/**
 * Get comment text
 * @param item
 * @returns
 */
function getCommentContext(item: NotificationActivityEnrichedRecord): string {
  let ops: ChatMessageOp[] = [];
  try {
    ops = JSON.parse(item.comment?.text ?? '[]') as unknown as ChatMessageOp[];
  } catch {
    // do nothing
  }
  const text = deltaToText(ops);
  return text.length > 100 ? `${text.slice(0, 100)}...` : text;
}

/**
 * Get url for chat page
 * @param item
 */
function getChatPageUrl(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}): string {
  const chanelId = getChatChannelId(item);
  let url = `/space/${item.workspaceId}/chat/${chanelId}`;
  if (options.mode === 'portals') {
    url = `/chats-dashboard`;
    const page = getBreadcrumbsPart(item, 'page');
    if (page) {
      url = page?.url ?? '';
    }
  }
  return url;
}

/**
 * Get chat channel id
 * @param item
 * @returns
 */
function getChatChannelId(item: NotificationActivityEnrichedRecord): string {
  const extra = item.extra as unknown as ActivityChatExtra | undefined;
  if (!extra) return '';

  let [, , chanelId] = item.objectId.split('#');

  if (!chanelId.match(/_/)) {
    const extraChanelId = extra?.channel?.id;
    if (extraChanelId) {
      const [, , id] = extraChanelId.split('#') || [];
      if (id) {
        chanelId = id;
      }
    }
  }
  return chanelId;
}

/**
 * Get chat parent url
 * @param item
 */
function getChatParentUrl(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}): string {
  let url = `/space/${item.workspaceId}/chat`;
  if (options.mode === 'portals') {
    url = getChatPageUrl(item, options);
  }
  return url;
}

/**
 * Get chat message url
 * @param item
 * @returns
 */
function getChatContextUrl(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}): string {
  const extra = item.extra as unknown as ActivityChatExtra | undefined;
  if (!extra) return getChatPageUrl(item, options);

  const chanelId = getChatChannelId(item);

  let message = extra.message;
  if (extra.messages) {
    message = extra.messages[0];
  }

  let url = getChatPageUrl(item, options);
  if (message) {
    url = `/space/${item.workspaceId}/chat/${chanelId}/${message.timestamp}`;
  }

  if (options.mode === 'portals') {
    url = getChatPageUrl(item, options);
  }
  return url;
}

/**
 * Get chat title
 * @param item
 */
function getChatTitle(item: NotificationActivityEnrichedRecord): string {
  const extra = item.extra as unknown as ActivityChatExtra;
  if (extra.isDM) {
    return '';
  }
  return extra.channel.name ?? 'Channel';
}

/**
 * Get task page url
 * @param item
 */
function getTaskPageUrl(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}): string {
  let url = `/space/${item.workspaceId}/tasks/${item.extra.taskId}`;
  if (options.mode === 'portals') {
    url = `/tasks-dashboard?task-id=${item.extra.taskId}`;
  }
  return url;
}

/**
 * Get task parent url
 * @param item
 */
function getTaskParentUrl(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}): string {
  let url = `/space/${item.workspaceId}/tasks`;
  if (options.mode === 'portals') {
    url = `/tasks-dashboard`;
  }
  return url;
}

/**
 * Get task item disabled
 * @param item
 * @returns
 */
function getTaskItemDisabled(item: NotificationActivityEnrichedRecord): boolean {
  return !!item.task?.deleted;
}

/**
 * Get task open portal page in new tab
 * @param item
 * @param options
 * @returns
 */
function getTaskOpenPortalPageInNewTab(
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {}
): boolean {
  let openInNewTab = true;
  if (options.mode === 'webnotes') {
    openInNewTab = false;
  }
  return openInNewTab;
}

/**
 * Get Bucket Attachment title
 * @param item
 */
function getAttachmentTitle(item: NotificationActivityEnrichedRecord): string {
  const { totalFiles = 0, fileName = '' } = item.extra ?? {};
  let title = fileName;

  if (totalFiles > 1) {
    const leftFiles = totalFiles - 1;
    title = `${fileName} and ${leftFiles} more file${leftFiles > 1 ? 's' : ''}`;
  }

  return title;
}

/**
 * Get Bucket Attachment page url
 * @param item
 */
function getAttachmentPageUrl(
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {}
): string {
  let url = `/space/${item.workspaceId}/files?globalId=${item.objectId}`;

  if (options.mode === 'portals') {
    url = `/files-dashboard?globalId=${item.objectId}`;
  }

  return url;
}

/**
 * Get Bucket Attachment parent url
 * @param item
 * @param options
 * @returns
 */
function getAttachmentParentUrl(
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {}
): string {
  let url = `/space/${item.workspaceId}/files`;

  if (options.mode === 'portals') {
    url = `/files-dashboard`;
  }

  return url;
}

/**
 * Get Bucket Attachment item disabled
 * @param item
 * @returns
 */
function getAttachmentItemDisabled(item: NotificationActivityEnrichedRecord): boolean {
  return !item.file;
}

/**
 * Get activity action root
 * @param item
 * @returns
 */
function getActivityActionRoot(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}) {
  let actionRootUrl = item.actionPlaceUrl ?? '';
  let actionRootTitle = item.actionPlace ?? '';

  if (item.portalId && options.mode === 'webnotes') {
    actionRootUrl = `https://${options.portalDomain}`;
    actionRootTitle = options.portalName ?? '';
  }

  if (item.extra.breadcrumbs) {
    const root = getBreadcrumbsPart(item, 'domain');
    if (root) {
      // TODO: here have create url with auth for cname
      actionRootUrl = root.url;
      actionRootTitle = root.name;
    }
  }

  return { actionRootUrl, actionRootTitle };
}

/**
 * Get note action root
 * @param item
 * @returns
 */
function getNoteActionRoot(item: NotificationActivityEnrichedRecord, options: GetActivityParamsOptions = {}) {
  let actionRootUrl = item.actionPlaceUrl ?? '';
  let actionRootTitle = item.actionPlace ?? '';

  if (item.portalId && options.mode === 'webnotes' && item.note?.isSharedForPortal) {
    actionRootUrl = `https://${options.portalDomain}`;
    actionRootTitle = options.portalName ?? '';
  }

  if (item.note?.isSharedForPortal && item.extra.breadcrumbs) {
    const root = getBreadcrumbsPart(item, 'domain');
    if (root) {
      // TODO: here have create url with auth for cname
      actionRootUrl = root.url;
      actionRootTitle = root.name;
    }
  }

  return { actionRootUrl, actionRootTitle };
}

interface GetActivityParamsResult {
  title: string;
  url: string;
  itemDisabled: boolean;
  parentDisabled: boolean;
  itemParentTitle: string | null | undefined;
  parentIcon: ReactElement;
  parentUrl: string;
  contextUrl: string;
  context: string;
  openPortalPageInNewTab: boolean;
  openPortalRootInNewTab: boolean;
  actionRootUrl: string;
  actionRootTitle: string;
}

interface GetActivityParamsOptions {
  mode?: AtivityItemMode;
  portalDomain?: string;
  portalName?: string;
}

/**
 * Get activity item parem to show
 * @param t expects global localization scope
 * @param item
 * @param options
 * @returns
 */
export const getActivityParams = (
  item: NotificationActivityEnrichedRecord,
  options: GetActivityParamsOptions = {},
  t: TranslatesFnType
): GetActivityParamsResult => {
  let title = '';
  let url = '';
  let parentTitle = item.parentTitle === 'noParent' ? '' : item.parentTitle;
  let itemDisabled = false;
  let parentDisabled = false;
  let parentIcon: ReactElement | null = IconsMap.FOLDER;
  let parentUrl = '';
  let contextUrl = '';
  let context = '';
  let openPortalPageInNewTab = true;
  let openPortalRootInNewTab = true;
  let { actionRootUrl, actionRootTitle } = getActivityActionRoot(item, options);

  // Note
  // User
  // Folder
  // Workspace
  if (['note', 'user', 'folder', 'workspace'].includes(item.objectType)) {
    title = getNotePageTitle(item);
    url = getNotePageUrl(item);
    ({ actionRootUrl, actionRootTitle } = getNoteActionRoot(item, options));
    itemDisabled = getNoteItemDisabled(item, options);
    parentDisabled = getNoteParentDisabled(item, options);
    parentUrl = getNoteParentUrl(item);
    openPortalPageInNewTab = item.note?.isSharedForPortal ?? false;
    openPortalRootInNewTab = item.note?.isSharedForPortal ?? false;
  }

  // Comment
  if (item.objectType === 'comment') {
    title = getCommentTitle(item);
    url = getCommentPageUrl(item, options);
    parentTitle = getCommentParentTitle(item);
    parentIcon = getCommentParentIcon(item);
    itemDisabled = getCommentItemDisabled(item);
    parentDisabled = getCommentParentDisabled(item);
    parentUrl = getCommentParentUrl(item, options);
    openPortalPageInNewTab = getCommentOpenPortalPageInNewTab(item, options);
    context = getCommentContext(item);
    contextUrl = url;
  }

  // Chat
  if (item.objectType === 'chat') {
    title = getChatTitle(item);
    url = getChatPageUrl(item, options);
    parentTitle = t('webnotes.webnotes-chat');
    parentIcon = IconsMap.CHAT_1;
    parentUrl = getChatParentUrl(item, options);
    contextUrl = getChatContextUrl(item, options);
    context = getChatContext(item.extra as unknown as ActivityChatExtra) ?? '';
    itemDisabled = getChatItemDisabled(item, options);
    parentDisabled = getChatParentDisabled(item, options);
    openPortalPageInNewTab = false;
  }

  // Task
  if (item.objectType === 'task') {
    title = item.task?.title ?? '-';
    url = getTaskPageUrl(item, options);
    parentTitle = t('webnotes.webnotes-tasks');
    parentIcon = IconsMap.TASKS;
    parentUrl = getTaskParentUrl(item, options);
    itemDisabled = getTaskItemDisabled(item);
    openPortalPageInNewTab = getTaskOpenPortalPageInNewTab(item, options);
  }

  // BucketAttachment
  if (item.objectType === 'bucketAttachment') {
    title = getAttachmentTitle(item);
    url = getAttachmentPageUrl(item, options);
    parentIcon = IconsMap.PAPERCLIP;
    parentTitle = t('webnotes.webnotes-files');
    parentUrl = getAttachmentParentUrl(item, options);
    itemDisabled = getAttachmentItemDisabled(item);
    openPortalPageInNewTab = false;
  }

  return {
    title,
    url: replaceNimbusDomains(url, 'base'),
    itemDisabled,
    parentDisabled,
    itemParentTitle: parentTitle,
    parentIcon,
    parentUrl,
    contextUrl: replaceNimbusDomains(contextUrl, 'base'),
    context,
    openPortalPageInNewTab,
    openPortalRootInNewTab,
    actionRootUrl,
    actionRootTitle,
  };
};
